/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      this.initialMuted = 'muted' in this.dataset ? this.dataset.muted !== 'false' : this.autoplay;
      this.viewportIntersecting = false;
      /** Until IntersectionObserver reports, viewportIntersecting is uninitialized false — must not pause on that. */
      this._hasViewportIntersectionReading = false;
      this.slideshowSuppressed = false;
      /** User tapped play before YouTube/Vimeo iframe API finished initialising */
      this.pendingProgrammaticPlay = false;
      /** iframe embed is usable (YT onReady / Vimeo Player.ready resolved) */
      this._embedApiReady = false;
    }

    connectedCallback() {
      this.init();
      this.boundVisibilityPlayback = window.debounce(() => {
        this.applyVisibilityPlayback();
      }, 120);
      window.addEventListener('resize', this.boundVisibilityPlayback);
    }

    disconnectedCallback() {
      if (this.boundVisibilityPlayback) {
        window.removeEventListener('resize', this.boundVisibilityPlayback);
      }
    }

    /**
     * Slideshow hides off-screen slides: ignore intersection while suppressed.
     * @param {boolean} suppress — true when parent slide is not active.
     */
    setSlideshowSuppressed(suppress) {
      this.slideshowSuppressed = Boolean(suppress);
      if (this.slideshowSuppressed) this.pause();
      else this.applyVisibilityPlayback();
    }

    /** True when the component may play: active slideshow slide & not display-hidden by responsive CSS */
    isPlayableContext() {
      const slide = this.closest('.slideshow__slide');
      if (slide && !slide.classList.contains('is-active')) return false;

      let node = this;
      while (node && node.nodeType === Node.ELEMENT_NODE) {
        const st = window.getComputedStyle(node);
        if (st.display === 'none' || st.visibility === 'hidden') return false;
        node = node.parentElement;
      }
      return true;
    }

    /**
     * Applies play/pause from viewport intersection, slideshow suppression, and responsive visibility.
     */
    applyVisibilityPlayback() {
      if (!this.player) return;

      const visible = !this.slideshowSuppressed && this.viewportIntersecting && this.isPlayableContext();

      if (!visible) {
        this.inViewport = false;
        /* Manual controls: never viewport-pause (IO often mis-reads during iframe inject / unmute). Autoplay only. */
        if (this.autoplay && this._hasViewportIntersectionReading) {
          this.pause();
        }
        const section = this.closest('.video-section');
        if (section) section.classList.remove('video-section--autoplay-paused');
        return;
      }

      this.inViewport = true;

      if (!this.autoplay) {
        this.syncAutoplayPauseOverlay();
        return;
      }

      if (this.pausedByUser) return;

      if (this.type === 'youtube') {
        if (typeof this.player.playVideo === 'function') this.player.playVideo();
      } else {
        try {
          this.player.play?.();
        } catch (err) {
          /* ignore */
        }
      }

      this.syncAutoplayPauseOverlay();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Failed autoplay: tap the section to start. Do not hook taps that land on
      // the <video> itself — iOS native controls (pause / ±10s) live there, and a
      // parent play() call races them (native pause, then pausedByUser blocks resume).
      if (this.background) {
        const section = this.closest('.video-section');
        if (section) {
          section.addEventListener('click', (evt) => {
            if (this.type === 'tag' && this.player && (evt.target === this.player || this.player.contains(evt.target))) {
              return;
            }
            this.play();
          });
        }
      }
    }

    /**
     * Autoplay video section: show CMS blocks only while paused and in view (native controls stay clickable while playing).
     */
    syncAutoplayPauseOverlay() {
      if (!this.autoplay || !this.player) return;
      const section = this.closest('.video-section');
      if (!section || !section.classList.contains('video-section--autoplay')) return;

      const apply = (isPaused) => {
        const inView = this.inViewport !== false;
        section.classList.toggle('video-section--autoplay-paused', Boolean(isPaused && inView));
      };

      if (this.type === 'youtube') {
        apply(this.player.getPlayerState() === YT.PlayerState.PAUSED);
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => apply(paused));
      } else if (this.type === 'tag') {
        apply(this.player.paused);
      }
    }

    /**
     * Vimeo (and similar): play/pause events are authoritative — don’t rely on async getPaused()
     * right after pause, and don’t let a follow-up sync clear the overlay.
     */
    syncAutoplayPauseOverlayFromEvent(isPaused) {
      if (!this.autoplay) return;
      const section = this.closest('.video-section');
      if (!section || !section.classList.contains('video-section--autoplay')) return;
      const inView = this.inViewport !== false;
      section.classList.toggle('video-section--autoplay-paused', Boolean(isPaused && inView));
    }

    /**
     * Intersection target for play/pause: prefer the section’s aspect-ratio box. Observing
     * `video-component` fails when autoplay sets width from clientHeight before layout (0×0),
     * so `inViewport` stays false and pause never reveals CMS blocks (seen often with Vimeo).
     */
    getViewportObserveRoot() {
      return this.closest('.height__image') || this;
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0,
              /* Manual play: start muted so playVideo() can run after onReady (outside user-gesture); unmute on PLAYING if !initialMuted */
              mute: !this.autoplay ? 1 : (this.autoplay || this.initialMuted ? 1 : 0)
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          /* Always start muted so deferred play() works; manual + sound turns off mute on first play (see below) */
          muted: true,
          controls: !this.background
        });

        await this.player.ready();
        try {
          await this.player.setMuted(true);
        } catch (err) {
          /* ignore */
        }
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.autoplay && this.inViewport !== false && !this.slideshowSuppressed) this.pausedByUser = true;
          if (this.dataset.toggleControlsOverlay === 'true') {
            this.closest('.slideshow-slide-video-scope')?.classList.remove('slideshow-slide-video-scope--playing');
          }
          this.syncAutoplayPauseOverlayFromEvent(true);
        });

        this.player.on('play', () => {
          if (!this.autoplay && !this.initialMuted) {
            try {
              void this.player.setMuted(false);
            } catch (err) {
              /* ignore */
            }
          }
          const ss = this.closest('.slideshow-slide-video-scope');
          if (!ss) {
            this.closest('.video-section')?.classList.add('video-section--played');
          }
          if (ss && this.dataset.toggleControlsOverlay === 'true') {
            ss.classList.add('slideshow-slide-video-scope--playing');
          }
          this.syncAutoplayPauseOverlayFromEvent(false);
        });

        this.player.on('ended', () => {
          this.closest('.slideshow-slide-video-scope')
            ?.classList.remove('slideshow-slide-video-scope--playing');
        });

        this._embedApiReady = true;
        requestAnimationFrame(() => {
          this.applyVisibilityPlayback();
          this.flushPendingProgrammaticPlay();
        });
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * iOS fires pause while seeking (±10s). Wait until seeking is done so skip
     * does not look like a user pause (controls disappearing / video staying paused).
     * @param {function} fn
     */
    afterSeekSettled(fn) {
      const run = () => {
        if (!this.player) return;
        if (this.player.seeking) {
          this.player.addEventListener('seeked', run, { once: true });
          return;
        }
        window.setTimeout(() => {
          if (!this.player) return;
          if (this.player.seeking) {
            this.player.addEventListener('seeked', run, { once: true });
            return;
          }
          fn();
        }, 50);
      };
      run();
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      const onPlay = () => {
        this.pausedByUser = false;

        const ss = this.closest('.slideshow-slide-video-scope');
        if (!ss) {
          this.closest('.video-section')?.classList.add('video-section--played');
        }
        if (this.dataset.toggleControlsOverlay === 'true' && ss) {
          ss.classList.add('slideshow-slide-video-scope--playing');
        }

        this.syncAutoplayPauseOverlay();
      };

      this.player.addEventListener('pause', () => {
        this.afterSeekSettled(() => {
          if (!this.player.paused) return;

          if (this.autoplay && this.inViewport && !this.slideshowSuppressed) this.pausedByUser = true;

          const ss = this.closest('.slideshow-slide-video-scope');
          if (this.dataset.toggleControlsOverlay === 'true' && ss) {
            ss.classList.remove('slideshow-slide-video-scope--playing');
          }

          this.syncAutoplayPauseOverlay();
        });
      });

      this.player.addEventListener('ended', () => {
        const ss = this.closest('.slideshow-slide-video-scope');
        if (this.dataset.toggleControlsOverlay === 'true' && ss) {
          ss.classList.remove('slideshow-slide-video-scope--playing');
        }

        this.syncAutoplayPauseOverlay();
      });

      this.player.addEventListener('play', onPlay);

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        onPlay();
      } else {
        this.addObserver(this.getViewportObserveRoot());

        const deferredMedia = this.closest('deferred-media');
        if ((!deferredMedia || deferredMedia.loadTrigger !== 'click') && this.autoplay) {
          this.player.muted = true;
        }

        requestAnimationFrame(() => this.applyVisibilityPlayback());
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');
      /* Embed uses mute=1 when !autoplay so async play succeeds; PLAYING unmutes when merchant allows sound */
      this.player.mute();

      this._embedApiReady = true;
      requestAnimationFrame(() => {
        this.applyVisibilityPlayback();
        this.flushPendingProgrammaticPlay();
      });
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      const ytScope = this.closest('.slideshow-slide-video-scope');

      if (e.data === YT.PlayerState.PAUSED) {
        if (this.autoplay && this.inViewport && !this.slideshowSuppressed) this.pausedByUser = true;
        if (ytScope && this.dataset.toggleControlsOverlay === 'true') {
          ytScope.classList.remove('slideshow-slide-video-scope--playing');
        }
      } else if (e.data === YT.PlayerState.ENDED) {
        if (ytScope && this.dataset.toggleControlsOverlay === 'true') {
          ytScope.classList.remove('slideshow-slide-video-scope--playing');
        }
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background && this.autoplay && this.isPlayableContext() && !this.slideshowSuppressed && this.viewportIntersecting) {
          this.player.playVideo();
        }
      } else if (e.data === YT.PlayerState.PLAYING) {
        /* Unmute after play starts: async playVideo() is outside user-gesture, so we start muted then unmute here. */
        if (!this.autoplay && !this.initialMuted && typeof this.player.unMute === 'function') {
          try {
            this.player.unMute();
          } catch (err) {
            /* ignore */
          }
        }
        this.pausedByUser = false;
        if (!ytScope) {
          this.closest('.video-section')?.classList.add('video-section--played');
        }
        if (ytScope && this.dataset.toggleControlsOverlay === 'true') {
          ytScope.classList.add('slideshow-slide-video-scope--playing');
        }
      }
      if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.PLAYING) {
        this.syncAutoplayPauseOverlay();
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height (wider than 16:9 box = horizontal crop).
      if (this.autoplay && !this.naturalWidth) {
        const slideScope = this.closest('.slideshow-slide-video-scope');
        const measureAndSetWidth = () => {
          const h = slideScope?.clientHeight > 0 ? slideScope.clientHeight : this.clientHeight;
          if (h >= 2) {
            this.style.width = `${h * 1.7778}px`;
          }
        };
        let measureAttempts = 0;
        const measureUntilReady = () => {
          const h = slideScope?.clientHeight > 0 ? slideScope.clientHeight : this.clientHeight;
          if (h >= 2) {
            this.style.width = `${h * 1.7778}px`;
          } else if (measureAttempts < 60) {
            measureAttempts += 1;
            requestAnimationFrame(measureUntilReady);
          }
        };
        measureUntilReady();
        window.addEventListener('resize', window.debounce(measureAndSetWidth, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.getViewportObserveRoot());
    }

    /** Runs a play() that was queued before YouTube/Vimeo finished initialising */
    flushPendingProgrammaticPlay() {
      if (!this.pendingProgrammaticPlay) return;
      this.pendingProgrammaticPlay = false;
      if (!this.player || this.slideshowSuppressed) return;
      if (!this.isPlayableContext()) return;
      /* Manual mode: programmatic play resets user pause; autoplay still respects pausedByUser. */
      if (this.pausedByUser && this.autoplay) return;
      if (this.pausedByUser && !this.autoplay) this.pausedByUser = false;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else if (this.type === 'vimeo') {
        try {
          void this.player.play();
        } catch (err) {
          /* ignore */
        }
      }
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (this.slideshowSuppressed) return;

      /* Manual mode: programmatic play resets user pause; autoplay still respects pausedByUser. */
      if (this.pausedByUser && this.autoplay) return;
      if (this.pausedByUser && !this.autoplay) this.pausedByUser = false;

      if (this.type === 'youtube' || this.type === 'vimeo') {
        if (!this.player || !this._embedApiReady) {
          this.pendingProgrammaticPlay = true;
          return;
        }
      } else if (!this.player) {
        return;
      }

      if (!this.isPlayableContext()) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        this.player.play();
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) {
        this._hasViewportIntersectionReading = true;
        this.viewportIntersecting = true;
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this._hasViewportIntersectionReading = true;
          this.viewportIntersecting = entry.isIntersecting;
          this.applyVisibilityPlayback();
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}

if (!customElements.get('video-section-play-button')) {
  class VideoSectionPlayButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeyDown);
    }

    handleKeyDown(evt) {
      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault();
        this.handleClick(evt);
      }
    }

    handleClick(evt) {
      const scopeRoot = this.closest('.slideshow-slide-video-scope')
        || this.closest('.video-section');
      if (!scopeRoot) return;

      const deferredMedia = scopeRoot.querySelector('deferred-media');
      if (!deferredMedia || typeof deferredMedia.loadContent !== 'function') return;

      const wasLoaded = deferredMedia.hasAttribute('loaded');
      const loadBtn = deferredMedia.querySelector('.js-load-media');

      if (!wasLoaded && loadBtn) {
        loadBtn.click();
      } else if (!wasLoaded) {
        deferredMedia.loadContent(true, true, 'click');
      }

      const isSlideshowManual = Boolean(this.closest('.slideshow-slide-video-scope'));
      const videoSectionRoot = scopeRoot.classList.contains('video-section')
        ? scopeRoot
        : scopeRoot.closest('.video-section');
      if (!isSlideshowManual && videoSectionRoot) {
        const delay = typeof Shopify !== 'undefined' && Shopify.designMode ? 500 : 1500;
        setTimeout(() => videoSectionRoot.classList.add('video-section--played'), delay);
      }

      const tryPlay = () => {
        const vc = deferredMedia.querySelector('video-component');
        if (!vc || typeof vc.play !== 'function') return;
        vc.pausedByUser = false;
        vc.play();
      };

      if (!wasLoaded) {
        requestAnimationFrame(() => {
          requestAnimationFrame(tryPlay);
        });
      } else {
        tryPlay();
      }
    }
  }

  customElements.define('video-section-play-button', VideoSectionPlayButton);
}

/** Vimeo section posters: official still from oEmbed (Liquid has no hosted thumbnail URL). */
(function loadVimeoSectionPosters() {
  document.querySelectorAll('img[data-vimeo-poster-url]:not([data-vimeo-poster-done])').forEach((img) => {
    const pageUrl = img.getAttribute('data-vimeo-poster-url');
    if (!pageUrl) return;
    img.setAttribute('data-vimeo-poster-done', '');
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(pageUrl)}`)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((data) => {
        if (data.thumbnail_url) img.src = data.thumbnail_url;
      })
      .catch(() => {});
  });
}());
